import { ArrowRight, ShieldCheck } from "lucide-react";
import { scroller } from "react-scroll";
import { Trans, useTranslation } from "@thyra/landing-i18n";
import { getAppUrl } from "../env";
import { HeroShowcase } from "./HeroShowcase";

const SCROLL_OPTIONS = { smooth: true, duration: 650, offset: -88 };

export function Hero() {
  const { t } = useTranslation("home");

  const scrollToHumanTasks = () => {
    scroller.scrollTo("human-tasks", SCROLL_OPTIONS);
  };

  return (
    <section
      id="home"
      className="relative isolate w-full overflow-x-clip border-b border-border/60 px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-14 md:px-8 lg:pt-16"
    >
      {/* Backdrop: dotted grid + soft brand glow */}
      <div
        className="bg-dot-grid pointer-events-none absolute inset-0"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[-12rem] h-[26rem] w-[42rem] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(ellipse at center, var(--aurora-1) 0%, var(--aurora-3) 45%, transparent 75%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col items-center text-center">
        <div
          className="hero-card-enter mb-5 inline-flex max-w-full items-center gap-2 rounded-full border border-primary/20 bg-background/80 px-4 py-1.5 text-xs font-medium text-muted-foreground-strong shadow-sm backdrop-blur-xl sm:text-sm dark:bg-zinc-900/70"
          style={{ animationDelay: "0ms" }}
        >
          <ShieldCheck
            className="h-4 w-4 shrink-0 text-primary"
            strokeWidth={2}
          />
          <span>{t("hero.badge")}</span>
        </div>

        <h1 className="hero-card-enter heading-gradient max-w-4xl text-balance text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl md:text-6xl lg:text-[4rem]">
          <Trans
            t={t}
            i18nKey="hero.title"
            components={{
              accent: (
                <span className="block bg-[linear-gradient(120deg,var(--blue-500),var(--blue-700))] bg-clip-text pb-1.5 pt-1 text-transparent" />
              ),
            }}
          />
        </h1>

        <p
          className="hero-card-enter mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg"
          style={{ animationDelay: "80ms" }}
        >
          {t("hero.description")}
        </p>

        <div
          className="hero-card-enter mt-8 flex w-full max-w-md flex-col items-stretch gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
          style={{ animationDelay: "160ms" }}
        >
          <a
            href={getAppUrl()}
            className="btn-brand group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-7 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem]"
          >
            {t("hero.cta.primary")}
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </a>
          <button
            type="button"
            onClick={scrollToHumanTasks}
            className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-border bg-background/80 px-7 text-sm font-semibold text-foreground shadow-sm backdrop-blur-sm transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:w-auto sm:min-w-[12rem] dark:bg-zinc-900/70"
          >
            {t("hero.cta.secondary")}
          </button>
        </div>

        <p
          className="hero-card-enter mt-4 text-xs text-muted-foreground sm:text-sm"
          style={{ animationDelay: "220ms" }}
        >
          {t("hero.note")}
        </p>

        <div className="mt-12 w-full sm:mt-14">
          <HeroShowcase />
        </div>
      </div>
    </section>
  );
}
